import React from 'react';
import { Box, Typography, Card, CardContent, Button, Stack, useTheme } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useTranslation } from 'react-i18next';
import { IsTest } from 'config/config';
import CertificationPageHero from '../components/CertificationPageHero';

export default function UserGuidePage() {
  const { t } = useTranslation();
  const theme = useTheme();

  const langMap = {
    EN: t('language.english'),
    FR: t('language.french'),
    DE: t('language.german'),
    ES: t('language.spanish'),
    IT: t('language.italian'),
    FI: t('language.finnish'),
    SV: t('language.swedish'),
    NO: t('language.norwegian'),
  };

  const guides = IsTest
    ? [
        {
          title: 'Brand representation guide 2024',
          language: langMap.EN,
          description: 'Official brand representation guidelines for 2024.',
          url: 'https://brp--nplus1.sandbox.my.salesforce-sites.com/0693x00000MU2uD',
        },
        {
          title: 'Guide de représentation de la marque 2024',
          language: langMap.FR,
          description: 'Lignes directrices officielles de représentation de la marque 2024.',
          url: 'https://brp--nplus1.sandbox.my.salesforce-sites.com/0693x00000FYLBW',
        },
      ]
    : [
        {
          title: 'Retail Environment Guidebook',
          language: langMap.EN,
          description:
            "To learn more about our services, the retail environment criteria within the dealer certification program, exterior signage and BRP's additional recommendations, read the Retail Environment Guidebook!",
          url: 'https://brp.my.salesforce-sites.com/0693x00000MU2uD',
        },
        {
          title: "Guide de l'environnement de vente au détail",
          language: langMap.FR,
          description:
            "Pour en apprendre davantage sur nos services, les critères de l'environnement de vente au détail de la certification des concessionnaires, les enseignes extérieures et les recommandations supplémentaires BRP, consultez le Guide de l'environnement de vente au détail!",
          url: 'https://brp.my.salesforce-sites.com/0693x00000FYLBW',
        },
        {
          title: "Critères pour l'environnement de vente au détail - Vidéo",
          language: langMap.FR,
          description:
            "Pour en apprendre davantage sur les critères de l'environnement de vente au détail de la certification des concessionnaires, visionnez cette vidéo!",
          url: 'https://www.youtube.com/watch?v=Crl34K2DndM',
        },
        {
          title: 'Retail Environment Criteria Video',
          language: langMap.EN,
          description:
            'To learn more about the retail environment criteria within the dealer certification program, watch this video!',
          url: 'https://www.youtube.com/watch?v=bzsGyxROKOg',
        },
        {
          title: 'Certification Photo Tool - User Guide',
          language: langMap.EN,
          description:
            'To learn how to use the new photo tool for the Retail Environment portion of the new dealer Certification Program, use the user guide or the tutorial for the Certification Photo Tool!',
          url: 'https://brp.my.salesforce.com/069OM00000T9UhX',
        },
        {
          title: "Outil photo pour la Certification - Guide de l'utilisateur",
          language: langMap.FR,
          description:
            "Pour vous guider avec l'utilisation du nouvel outil photo de l'environnement de vente au détail dans le cadre de la nouvelle certification des concessionnaires, utilisez le guide de l''utilisateur ou le tutoriel de l'outil photo de la certification !",
          url: 'https://brp.my.salesforce.com/069OM00000TOCyf',
        },
        {
          title: 'Certification Photo Tool - Tutorial Video',
          language: langMap.EN,
          description:
            'To learn how to use the new photo tool for the Retail Environment portion of the new dealer Certification Program, use the user guide or the tutorial for the Certification Photo Tool!',
          url: 'https://vimeo.com/1161927064/25c7853ef7?share=copy&fl=sv&fe=ci',
        },
        {
          title: 'Outil photo pour la Certification - Vidéo tutoriel',
          language: langMap.FR,
          description:
            "Pour vous guider avec l'utilisation du nouvel outil photo de l'environnement de vente au détail dans le cadre de la nouvelle certification des concessionnaires, utilisez le guide de l'utilisateur ou le tutoriel de l'outil photo de la certification !",
          url: 'https://vimeo.com/1162215029/1033b1ecc6?share=copy&fl=sv&fe=ci',
        },
      ];

  return (
    <Box>
      <CertificationPageHero
        image="/images/certification/carousel-img-en-03.jpg"
        title={t('certNav.userGuide')}
        subtitle={t('certNav.userGuideSubtitle')}
      />
      <Box
        sx={{
          maxWidth: { xs: '100%', md: 1200, lg: 1400, xl: 1600 },
          mx: 'auto',
          px: { xs: 2, md: 3 },
          py: { xs: 2, md: 3 },
        }}>
        <Typography variant="h2" sx={{ mb: 3 }}>
          {t('certNav.userGuide')}
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
            gap: 3,
          }}>
          {guides.map((guide, index) => (
            <Card
              key={index}
              sx={{
                bgcolor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
                transition: 'all 0.3s ease',
                boxShadow: theme.shadows[4],
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                '&:hover': {
                  borderColor: theme.palette.secondary.main,
                  transform: 'translateY(-2px)',
                  boxShadow: theme.shadows[8],
                },
              }}>
              <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', textAlign: 'center' }}>
                <Stack spacing={2} sx={{ width: '100%', flex: 1 }}>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h1" sx={{ fontWeight: 700, fontSize: { sm: 20, md: 24 } }}>
                      {guide.title}
                    </Typography>
                    <Typography variant="subtitle2" color="text.secondary">
                      {guide.language}
                    </Typography>
                    {guide.description && (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {guide.description}
                      </Typography>
                    )}
                  </Box>
                </Stack>
                <Button
                  variant="contained"
                  color="primary"
                  component="a"
                  href={guide.url}
                  target="_blank"
                  rel="noreferrer"
                  endIcon={<OpenInNewIcon />}
                  sx={{
                    textTransform: 'uppercase',
                    fontWeight: 700,
                    mt: 8,
                    alignSelf: 'center',
                  }}>
                  <Typography variant="h1" sx={{ fontSize: { sm: 18, md: 24 }, p: 2 }}>
                    {t('userGuides.openGuide')}
                  </Typography>
                </Button>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Box>
    </Box>
  );
}
